import { makeAutoObservable } from 'mobx'
import type { IDropdownItem } from 'shared/ui'
import { ChatbotBookingType, type IChatbotField, type IBookingType } from 'entities/Chatbot'
import { formatWidgetMessage } from 'pages/chatbot/ui/UnifiedAgent/lib/formatWidgetMessage'
import type { ChatStore } from 'pages/chatbot/ui/UnifiedAgent/ui/Chat/store/chatStore'
import { type InstructionsSectionStore } from 'pages/chatbot/ui/UnifiedAgent/ui/InstructionsSection/store/instructionsSectionStore'
import type { BookingSectionStore } from './bookingSectionStore'

const TOTAL_STEPS = 4

export const STEP_HEADERS = [
  'What scheduling app do you use?',
  'Which event link should the agent use?',
  'How should customers book meetings?',
  'Should the agent handle reminders, cancellations, and reschedules automatically?',
]

export type IWidgetOption = { id: IBookingType; label: string; tooltip?: string }

export const BOOKING_METHOD_OPTIONS: IWidgetOption[] = [
  {
    id: ChatbotBookingType.TimeChat,
    label: 'Chat to book a time',
    tooltip: 'The AI Agent will text back and forth with the contact to schedule a time',
  },
  {
    id: ChatbotBookingType.ShareLink,
    label: 'Share booking link',
    tooltip: 'The AI Agent will share the booking link so the contact can choose a time',
  },
]
export const BOOKING_REMINDERS_OPTIONS = [
  { id: 'yes', label: 'Yes' },
  { id: 'no', label: 'No' },
]

export class BookingWidgetStore {
  step = 1
  focusedIndex = 0
  search = ''
  isSelectingIntegration = false
  isSkipped = false
  localSelectedConnection: IDropdownItem | null = null
  localEventLink: IDropdownItem | null = null
  localBookingType: IBookingType
  localSmartBookingEnabled: boolean
  localBookingFields: IChatbotField[] | null = null

  constructor(
    readonly sectionStore: BookingSectionStore,
    readonly chatStore: ChatStore,
    readonly instructionsSectionStore: InstructionsSectionStore
  ) {
    this.localBookingType = sectionStore.bookingType
    this.localSmartBookingEnabled = sectionStore.smartBookingEnabled
    makeAutoObservable(this)
  }

  get isLocalHubspotSelected(): boolean {
    if (!this.localSelectedConnection) return false
    return this.sectionStore.isHubspotConnection(this.localSelectedConnection)
  }

  get totalSteps() {
    return TOTAL_STEPS
  }

  get isLastStep() {
    return this.step === TOTAL_STEPS
  }

  get submitMessage(): string {
    if (this.isSkipped)
      return formatWidgetMessage(STEP_HEADERS.map((header) => ({ q: header, a: 'Skip' })))

    return formatWidgetMessage([
      { q: STEP_HEADERS[0], a: this.localSelectedConnection?.label ?? '' },
      { q: STEP_HEADERS[1], a: this.localEventLink?.label ?? '' },
      {
        q: STEP_HEADERS[2],
        a:
          BOOKING_METHOD_OPTIONS.find((option) => option.id === this.localBookingType)?.label ?? '',
      },
      { q: STEP_HEADERS[3], a: this.localSmartBookingEnabled ? 'Yes' : 'No' },
    ])
  }

  get currentListLength() {
    switch (this.step) {
      case 1:
        return this.sectionStore.connectionList.length
      case 2:
        return this.sectionStore.meetingsList.length
      case 3:
        return this.isLocalHubspotSelected ? 1 : BOOKING_METHOD_OPTIONS.length
      default:
        return BOOKING_REMINDERS_OPTIONS.length
    }
  }

  get safeIndex() {
    if (!this.currentListLength) return 0
    const raw = Math.min(this.focusedIndex, this.currentListLength - 1)
    if (this.step === 2) {
      const meetings = this.sectionStore.meetingsList
      if (meetings[raw]?.disabled) {
        const firstEnabled = meetings.findIndex((m) => !m.disabled)
        return firstEnabled === -1 ? raw : firstEnabled
      }
    }
    return raw
  }

  setFocusedIndex = (index: number) => {
    this.focusedIndex = index
  }

  moveFocus = (delta: number) => {
    if (!this.currentListLength) return
    let next = Math.min(Math.max(this.safeIndex + delta, 0), this.currentListLength - 1)
    if (this.step === 2) {
      const meetings = this.sectionStore.meetingsList
      while (meetings[next]?.disabled) {
        const candidate = next + delta
        if (candidate < 0 || candidate >= this.currentListLength) return
        next = candidate
      }
    }
    this.focusedIndex = next
  }

  setSearch = (value: string) => {
    this.search = value
    this.focusedIndex = 0
    if (this.step === 2) this.sectionStore.setSearch(value)
  }

  private resetStepState = () => {
    this.focusedIndex = 0
    this.search = ''
    this.sectionStore.setSearch('')
  }

  private advanceStep = () => {
    this.step++
    this.resetStepState()
  }

  private selectedIndexForStep = (step: number): number => {
    switch (step) {
      case 1: {
        const id = (this.localSelectedConnection ?? this.sectionStore.selectedConnectionItem)?.id
        if (id === undefined) return 0
        return Math.max(
          0,
          this.sectionStore.connectionList.findIndex((item) => item.id === id)
        )
      }
      case 2: {
        const id = this.localEventLink?.id
        if (id === undefined) return 0
        return Math.max(
          0,
          this.sectionStore.meetingsList.findIndex((item) => item.id === id)
        )
      }
      case 3: {
        return Math.max(
          0,
          BOOKING_METHOD_OPTIONS.findIndex((o) => o.id === this.localBookingType)
        )
      }
      default:
        return 0
    }
  }

  goBack = () => {
    this.step = Math.max(1, this.step - 1)
    this.search = ''
    this.sectionStore.setSearch('')
    this.focusedIndex = this.selectedIndexForStep(this.step)
  }

  reset = () => {
    this.step = 1
    this.resetStepState()
    this.localSelectedConnection = null
    this.localEventLink = null
    this.localBookingType = this.sectionStore.bookingType
    this.localSmartBookingEnabled = this.sectionStore.smartBookingEnabled
    this.localBookingFields = null
  }

  private selectOnStep1 = async (): Promise<void> => {
    const item = this.sectionStore.connectionList[this.safeIndex]
    if (!item) return

    this.isSelectingIntegration = true
    try {
      await this.sectionStore.setConnection(item, true)
    } finally {
      this.isSelectingIntegration = false
    }

    if (this.sectionStore.isIntegrationReadyForWidget(item)) {
      this.localSelectedConnection = item
      this.localEventLink = null
      this.advanceStep()
    }
  }

  private selectOnStep2 = () => {
    const item = this.sectionStore.meetingsList[this.safeIndex]
    this.localEventLink = item
    if (this.localSelectedConnection) {
      void this.sectionStore
        .fetchWidgetBookingFields(this.localSelectedConnection, item)
        .then((fields) => {
          this.localBookingFields = fields
        })
    }
    this.advanceStep()
  }

  private selectOnStep3 = () => {
    const item = BOOKING_METHOD_OPTIONS[this.safeIndex]
    this.localBookingType = item.id
    this.advanceStep()
  }

  private submitStep4 = () => {
    const option = BOOKING_REMINDERS_OPTIONS[this.safeIndex]
    this.localSmartBookingEnabled = option.id === 'yes'
  }

  selectAt = async (index: number) => {
    this.focusedIndex = index
    await this.handleSelect()
  }

  handleSelect = async () => {
    switch (this.step) {
      case 1:
        await this.selectOnStep1()
        break
      case 2:
        this.selectOnStep2()
        break
      case 3:
        this.selectOnStep3()
        break
    }
  }

  handleClose = () => {
    this.isSkipped = true
    const message = this.submitMessage

    this.chatStore.sendMessage(message)
  }

  handleSubmit = () => {
    this.submitStep4()
    const message = this.submitMessage
    const localSelectedConnection = this.localSelectedConnection
    const localEventLink = this.localEventLink
    const localBookingType = this.localBookingType
    const localSmartBookingEnabled = this.localSmartBookingEnabled

    this.chatStore.setLocalWidgetState({
      settings: {
        book_appointment_enabled: true,
        book_appointment_type: localBookingType,
      },
      ...(this.localBookingFields && { booking_fields: this.localBookingFields }),
    })

    this.chatStore.sendMessage(message, async () => {
      if (localSelectedConnection) await this.sectionStore.setConnection(localSelectedConnection)
      if (localEventLink) this.sectionStore.setLink(localEventLink)
      this.sectionStore.setBookingType(localBookingType)
      this.sectionStore.setSmartBookingEnabled(localSmartBookingEnabled)
      this.sectionStore.setBookingEnabled(true)
      await this.sectionStore.init()
      this.instructionsSectionStore.setInstructions(this.instructionsSectionStore.instructions)
    })
  }
}
